import quickFetch from 'quick-fetch';
import { Fragment } from 'react';
import ReactDOM from 'react-dom';

//I18n Configuration
import BasicPublicI18nSetup from 'I18n/init/setup/BasicPublicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import 'I18n/init/moment/standardMomentLocales';
import 'I18n/init/moment/publicMomentLocales';
import connectI18nLoader from './connectI18nLoader';
import { RhumbProvider, NavMarker } from 'react-rhumb';
import staticAdapter from 'react-rhumb/staticAdapter';
import rhumbConfig from '../rhumb-config.yaml';
import Url from 'urlinator/Url';
import noAuthApiClient from 'hub-http/clients/noAuthApiClient';
import { getSlug } from 'MeetingsBase/utils/url';
import { mapFalsyParamsToNull, constructFormState } from 'MeetingsPublic/utils/urlParams';
import createBookingComponent from 'MeetingsLib/components/createBookingComponent';
import pick from 'transmute/pick';
import omit from 'transmute/omit';
import keySeq from 'transmute/keySeq';
import { ravenDataCallback } from 'MeetingsLib/utils/errorUtils';
import { LEGACY_RESCHEDULE_PARAM, RESCHEDULE_PARAM, LEGACY_MEETING_CHANGE_ID_PARAM } from 'MeetingsLib/constants/tracking';
import Raven from 'raven-js';
import UIApplication from 'UIComponents/app/UIApplication';
import { Metrics } from './utils/metrics';
import BookingFunnelMetricsMiddleware from './redux/middleware/BookingFunnelMetricsMiddleware';
import { jsx as _jsx } from "react/jsx-runtime";
const DatePickerMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "DATE_PICKER_BUTTON_LOAD"
});
const TimePickerMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "TIME_PICKER_LOAD"
});
const NotFoundMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "LINK_NOT_FOUND"
});
const ErrorMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "BOOKING_LOAD_FAILURE"
});
const EmailFormMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "EMAIL_FORM_LOAD"
});
const CancelMarker = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "CANCEL_LOAD"
});
export default class Application {
  constructor() {
    this.scriptStartTime = 0;
  }
  init() {
    if (window.performance && window.performance.getEntriesByName) {
      const scriptStartEntries = window.performance.getEntriesByName('scriptStartTime');
      if (scriptStartEntries && scriptStartEntries.length > 0) {
        this.scriptStartTime = scriptStartEntries[0].startTime;
        Raven.setExtraContext({
          scriptStartTime: scriptStartEntries[0].startTime,
          appStartTime: window.performance.now()
        });
      }
    }
    Raven.setDataCallback(ravenDataCallback);
    BasicPublicI18nSetup.setup({
      lang
    }).then(() => this.launch()).catch(error => {
      Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: {
          message: 'Error when initializing public I18n config'
        }
      });
    });
  }
  async launch() {
    await Promise.all([import(
    // @ts-ignore no type declarations for I18n data modules
    /* webpackChunkName: "i18n-allTimezones" */
    'I18n/init/data/allTimezones'),
    // @ts-ignore no type declarations for I18n data modules
    import( /* webpackChunkName: "i18n-currency" */'I18n/init/data/currency')]);
    const url = new Url(window.location);
    const slug = getSlug(window.location.href);
    const embedded = url.paramValue('embed') === 'true';
    const embeddedInChatbot = url.paramValue('embeddedInChatbot') === 'true';
    const oldRescheduleParam = url.paramValue(LEGACY_RESCHEDULE_PARAM) === 'true';
    const oldMeetingChangeId = url.paramValue(LEGACY_MEETING_CHANGE_ID_PARAM);
    const hasNewRescheduleParam = url.paramValue(RESCHEDULE_PARAM);
    const hasRescheduleParam = oldRescheduleParam || hasNewRescheduleParam;
    const meetingChangeId = oldMeetingChangeId || hasNewRescheduleParam;
    const isRescheduling = !embedded && hasRescheduleParam && !!meetingChangeId;
    Metrics.counter('app-mode', {
      embedded: String(embedded),
      rescheduling: String(isRescheduling)
    }).increment();
    const forceOffline = url.paramValue('forceOffline') === 'true';
    const forceMobile = url.paramValue('forceMobile') === 'true';
    const troubleshooting = url.paramValue('troubleshoot') === 'true';
    const urlParams = mapFalsyParamsToNull(url);
    const trackingParams = pick(['utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content', 'meeting_funnel', 'meeting_product', 'meeting_medium', 'meeting_type', 'meeting_source', 'meeting_campaign', 'source_app', 'hubs_signup-url', 'hubs_signup-cta', 'hubs_content', 'hubs_content-cta', 'hubs_offer', 'hubs_post', 'hubs_post-cta', 'ab', 'abStatus', 'contentId', 'referrer'], urlParams);
    const abTestingParams = pick(['ab', 'abStatus', 'contentId'], urlParams);
    let forcePropertyForm;
    try {
      forcePropertyForm = JSON.parse(url.paramValue('forcePropertyForm'));
    } catch (e) {
      forcePropertyForm = null;
    }
    const parentPageHSAnalyticsInfo = pick(['parentPageUrl', 'parentHubspotUtk'], urlParams);
    const formFieldUrlParams = omit(
    // @ts-ignore mismatched types
    keySeq(parentPageHSAnalyticsInfo),
    // @ts-ignore mismatched types
    omit(keySeq(trackingParams), urlParams));
    // @ts-ignore mismatched types
    const formState = constructFormState(formFieldUrlParams);
    const requestState = quickFetch.getRequestStateByName('book-info');
    const bookInfoFetchPreRender = !!(requestState && requestState.finished && !requestState.error);
    Raven.setExtraContext({
      bookInfoFetchPreRender
    });
    if (bookInfoFetchPreRender) {
      Metrics.counter('book-info-prerender-hit').increment();
    } else {
      Metrics.counter('book-info-prerender-miss').increment();
    }
    if (window.performance && window.performance.mark) {
      window.performance.mark('create-booking-component');
    }
    const BookingComponent = createBookingComponent({
      httpClient: noAuthApiClient,
      slug,
      embedded,
      forceOffline,
      forceMobile,
      troubleshooting,
      forcePropertyForm,
      trackingParams,
      abTestingParams,
      formState,
      /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
      isRescheduling,
      meetingChangeId,
      onError: () => Metrics.counter('booking-render-failure').increment(),
      parentPageHSAnalyticsInfo,
      allowTroubleshootMode: true,
      allowRedirect: true,
      additionalMiddleware: [BookingFunnelMetricsMiddleware]
    });

    /*  @ts-ignore Jun-18-2025, 19:10UTC fix types  */
    const WrappedComponent = connectI18nLoader(BookingComponent);
    const appRoute = document.getElementsByClassName('page')[0];

    // @ts-ignore mismatched types
    window.disabledHsPopups = ['LEADFLOW', 'FEEDBACK', 'CALLS_TO_ACTION'];
    if (embedded) {
      appRoute.className += ' embedded';

      // set disabled popups so that scripts don't load twice
      // (once on parent page and once in iframe) on embedded meetings
      // @ts-ignore mismatched types
      window.disabledHsPopups.push('PRIVACY', 'ADS', 'LIVE_CHAT');

      // set __hsUserToken as utk passed in from parent page so
      // analytics tracker will init with consistent utk across iframe and parent
      // otherwise, we have form submission and view with incorrect source
      // @ts-ignore mismatched types
      window.__hsUserToken = urlParams.parentHubspotUtk;
    }
    try {
      // eslint-disable-next-line react/no-deprecated
      ReactDOM.render( /*#__PURE__*/_jsx(UIApplication, {
        name: "MeetingsPublic",
        style: {
          overflowX: embeddedInChatbot ? 'hidden' : 'auto'
        },
        children: /*#__PURE__*/_jsx(Fragment, {
          children: /*#__PURE__*/_jsx(RhumbProvider, {
            config: rhumbConfig,
            history: staticAdapter('/'),
            children: /*#__PURE__*/_jsx(WrappedComponent
            // @ts-ignore prop types not yet forwarded
            , {
              hideLogo: embedded,
              DatePickerMarker: DatePickerMarker,
              TimePickerMarker: TimePickerMarker,
              NotFoundMarker: NotFoundMarker,
              ErrorMarker: ErrorMarker,
              EmailFormMarker: EmailFormMarker,
              CancelMarker: CancelMarker
            })
          })
        })
      }), appRoute);
      Metrics.counter('booking-render-success').increment();
      if (this.scriptStartTime > 0) {
        Metrics.timer('app-boot-duration').update(performance.now() - this.scriptStartTime);
      }
    } catch (error) {
      Raven.captureException(error instanceof Error ? error : new Error(String(error)), {
        extra: {
          message: 'Error loading booking component'
        }
      });
    }
  }
}